import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlbumList } from './components/AlbumList';
import { useConfirm } from './components/ConfirmDialog';
import { CoverArt } from './components/CoverArt';
import { CoverDialog } from './components/CoverDialog';
import { Login } from './components/Login';
import { NewsEditor } from './components/NewsEditor';
import { PublishDialog, VersionHistory } from './components/PublishDialog';
import { Toaster, useToasts } from './components/Toaster';
import { TrackList } from './components/TrackList';
import { UploadZone, type UploadProgress } from './components/UploadZone';
import { CloseIcon, NoteIcon, StarLogo } from './components/icons';
import {
  ApiError, DEFAULT_BITRATE, api, primaryRendition, signedUrl, uploadFile,
  type Album, type Grant, type Track,
} from './lib/api';
import { restoreSession, signOut, type Session } from './lib/auth';
import { formatDuration } from './lib/format';

type PublishPreview = {
  diff: { added: string[]; removed: string[]; changed: string[] };
  albumCount: number;
  trackCount: number;
};

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [booting, setBooting] = useState(true);
  // 曲库和官网动态是两套互不相干的东西，共用一个后台但不共用一个界面
  const [page, setPage] = useState<'music' | 'news'>('music');

  const [albums, setAlbums] = useState<Album[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [grant, setGrant] = useState<Grant | null>(null);

  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [publishPreview, setPublishPreview] = useState<PublishPreview | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [nowPlaying, setNowPlaying] = useState<{ url: string; name: string } | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverBusy, setCoverBusy] = useState(false);

  const { toasts, push } = useToasts();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const coverInputRef = useRef<HTMLInputElement>(null);

  const fail = useCallback((err: unknown) => {
    push((err as Error).message, 'error');
    // 会话确实没了就退回登录页。留在后台里只会让接下来每个操作都弹同一句话
    if (err instanceof ApiError && err.status === 401) setSession(null);
  }, [push]);

  useEffect(() => {
    restoreSession()
      .then(setSession)
      .finally(() => setBooting(false));
  }, []);

  const refreshAlbums = useCallback(async () => {
    try {
      const res = await api.listAlbums();
      setAlbums(res.albums);
      setSelectedId((current) => current ?? res.albums[0]?.albumId ?? null);
    } catch (err) {
      fail(err);
    }
  }, [fail]);

  const refreshTracks = useCallback(async (albumId: string) => {
    try {
      const res = await api.listTracks(albumId);
      setTracks(res.tracks.sort((a, b) => a.sortOrder - b.sortOrder));
    } catch (err) {
      fail(err);
    }
  }, [fail]);

  useEffect(() => {
    if (!session) return;
    refreshAlbums();
    api.grant().then(setGrant).catch(() => {});
    // 签名有效期 1 小时，提前续签，免得封面图突然变 403
    const timer = setInterval(() => api.grant().then(setGrant).catch(() => {}), 45 * 60 * 1000);
    return () => clearInterval(timer);
  }, [session, refreshAlbums]);

  useEffect(() => {
    if (selectedId) refreshTracks(selectedId);
    else setTracks([]);
  }, [selectedId, refreshTracks]);

  // 有曲目在转码时轮询，全部落定后自动停下来
  const pending = tracks.some((t) => t.status === 'transcoding');
  useEffect(() => {
    if (!pending || !selectedId) return;
    const timer = setInterval(() => refreshTracks(selectedId), 4000);
    return () => clearInterval(timer);
  }, [pending, selectedId, refreshTracks]);

  const selectedAlbum = useMemo(
    () => albums.find((a) => a.albumId === selectedId) ?? null,
    [albums, selectedId],
  );

  const readyCount = tracks.filter((t) => t.status === 'ready').length;
  const totalBytes = tracks.reduce((n, t) => n + (primaryRendition(t)?.bytes ?? 0), 0);
  const totalMs = tracks.reduce((n, t) => n + (t.durationMs ?? 0), 0);
  const bitrate = selectedAlbum?.audioBitrateKbps ?? DEFAULT_BITRATE;

  const createAlbum = async () => {
    try {
      const res = await api.createAlbum('New album');
      await refreshAlbums();
      setSelectedId(res.album.albumId);
      push('Album created. Click the title to rename it.');
    } catch (err) { fail(err); }
  };

  const renameAlbum = async (name: string) => {
    if (!selectedAlbum || name === selectedAlbum.name || !name.trim()) return;
    try {
      await api.updateAlbum(selectedAlbum.albumId, { name: name.trim() });
      await refreshAlbums();
    } catch (err) { fail(err); }
  };

  const toggleAlbumStatus = async () => {
    if (!selectedAlbum) return;
    const next = selectedAlbum.status === 'published' ? 'draft' : 'published';
    try {
      await api.updateAlbum(selectedAlbum.albumId, { status: next });
      await refreshAlbums();
      push(next === 'published'
        ? 'This album goes out with the next publish'
        : 'Moved to draft. It will not appear in the catalog.');
    } catch (err) { fail(err); }
  };

  const toggleSitePreview = async () => {
    if (!selectedAlbum) return;
    const next = !selectedAlbum.previewOnSite;
    try {
      await api.updateAlbum(selectedAlbum.albumId, { previewOnSite: next });
      await refreshAlbums();
      // 官网读的是数据库而不是已发布的清单，所以这个开关立刻生效，不用等 Publish
      push(next
        ? 'Now listed on the site. Visitors can stream it within ten minutes.'
        : 'Removed from the site.');
    } catch (err) { fail(err); }
  };

  const changeBitrate = async (kbps: number) => {
    if (!selectedAlbum || kbps === bitrate) return;
    try {
      await api.updateAlbum(selectedAlbum.albumId, { audioBitrateKbps: kbps });
      await refreshAlbums();
      push(`New uploads will encode at ${kbps}k. Existing tracks need a re-encode to catch up.`);
    } catch (err) { fail(err); }
  };

  const removeAlbum = async () => {
    if (!selectedAlbum) return;
    const ok = await confirm({
      title: `Delete “${selectedAlbum.name}”?`,
      body: 'This cannot be undone. An album has to be empty before it can be deleted.',
      confirmLabel: 'Delete album',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteAlbum(selectedAlbum.albumId);
      setSelectedId(null);
      await refreshAlbums();
      push('Album deleted');
    } catch (err) { fail(err); }
  };

  const uploadCover = async (file: File) => {
    if (!selectedAlbum) return;
    setCoverBusy(true);
    const contentType = file.type || 'image/png';
    try {
      const res = await api.requestCoverUpload(selectedAlbum.albumId, file.name, contentType);
      await uploadFile(res.uploadUrl, file, contentType, () => {});
      await refreshAlbums();
      setGrant(await api.grant());
      setCoverFile(null);
      push('Cover updated');
    } catch (err) {
      fail(err);
    } finally {
      setCoverBusy(false);
    }
  };

  const handleFiles = async (files: File[]) => {
    if (!selectedAlbum) return;
    const albumId = selectedAlbum.albumId;

    for (const file of files) {
      const id = `${file.name}-${Date.now()}-${Math.random()}`;
      const contentType = file.type || 'application/octet-stream';
      setUploads((list) => [...list, { id, filename: file.name, fraction: 0 }]);
      try {
        const { track, uploadUrl } = await api.requestUpload(albumId, file.name, contentType);
        await uploadFile(uploadUrl, file, contentType, (fraction) => {
          setUploads((list) => list.map((u) => (u.id === id ? { ...u, fraction } : u)));
        });
        await api.transcode(track.trackId, bitrate);
        setUploads((list) => list.filter((u) => u.id !== id));
        await refreshTracks(albumId);
      } catch (err) {
        setUploads((list) => list.map(
          (u) => (u.id === id ? { ...u, error: (err as Error).message } : u),
        ));
        fail(err);
      }
    }
  };

  const renameTrack = async (trackId: string, patch: { name?: string; artist?: string }) => {
    try {
      await api.updateTrack(trackId, patch);
      if (selectedId) await refreshTracks(selectedId);
    } catch (err) { fail(err); }
  };

  const reorderTracks = async (order: string[]) => {
    setTracks((list) => order.map((id) => list.find((t) => t.trackId === id)!).filter(Boolean));
    try {
      await api.reorderTracks(order);
    } catch (err) {
      fail(err);
      if (selectedId) await refreshTracks(selectedId);
    }
  };

  const reorderAlbums = async (order: string[]) => {
    setAlbums((list) => order.map((id) => list.find((a) => a.albumId === id)!).filter(Boolean));
    try {
      await api.reorderAlbums(order);
    } catch (err) {
      fail(err);
      await refreshAlbums();
    }
  };

  // 显式带上专辑当前的档：选择器就在同一屏上，改完设置点重转应该按新档走
  const retranscode = async (trackId: string) => {
    try {
      await api.transcode(trackId, bitrate);
      if (selectedId) await refreshTracks(selectedId);
      push(`Re-encoding at ${bitrate}k`);
    } catch (err) { fail(err); }
  };

  const retranscodeAlbum = async () => {
    if (!selectedAlbum) return;
    const targets = tracks.filter((t) => t.status !== 'uploading' && t.status !== 'transcoding');
    if (!targets.length) return;
    const ok = await confirm({
      title: `Re-encode all of “${selectedAlbum.name}”?`,
      body: `All ${targets.length} tracks will be re-encoded at ${bitrate}k. The masters are `
        + 'kept so nothing is lost, but it takes a while and MediaConvert bills for it.',
      confirmLabel: 'Re-encode',
    });
    if (!ok) return;

    let failed = 0;
    // 逐首提交而不是并发：MediaConvert 队列本来就是串行的，
    // 一次性灌进去只会让某一首排到很后面，还看不出是哪首出的错
    for (const track of targets) {
      try {
        await api.transcode(track.trackId, bitrate);
      } catch (err) {
        failed += 1;
        fail(err);
      }
    }
    await refreshTracks(selectedAlbum.albumId);
    if (failed < targets.length) {
      push(
        `Submitted ${targets.length - failed} tracks. The list refreshes as they finish.`,
        'success',
      );
    }
  };

  const removeTrack = async (track: Track) => {
    const ok = await confirm({
      title: `Delete “${track.name}”?`,
      body: 'The master file and both encoded renditions go with it. This cannot be undone.',
      confirmLabel: 'Delete track',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteTrack(track.trackId);
      if (selectedId) await refreshTracks(selectedId);
      push('Track deleted');
    } catch (err) { fail(err); }
  };

  const preview = async (track: Track) => {
    try {
      const res = await api.preview(track.trackId);
      setNowPlaying({ url: res.url, name: `${track.name} — ${track.artist}` });
    } catch (err) { fail(err); }
  };

  const openPublish = async () => {
    try {
      setPublishPreview(await api.previewPublish());
    } catch (err) { fail(err); }
  };

  if (booting) {
    return <div className="empty-state" style={{ height: '100vh' }}>Restoring your session…</div>;
  }
  if (!session) return <Login onSignedIn={setSession} />;

  return (
    <div className="shell">
      <header className="topbar">
        <div className="brand">
          <StarLogo />
          Cozy Room <em>Console</em>
        </div>

        <div className="segmented page-switch">
          <button className={page === 'music' ? 'on' : ''} onClick={() => setPage('music')}>
            Music
          </button>
          <button className={page === 'news' ? 'on' : ''} onClick={() => setPage('news')}>
            Site news
          </button>
        </div>

        <div className="spacer" />
        <span className="user-chip">{session.email}</span>
        {/* 发布与版本历史都是曲库清单的操作，切到官网页就没有意义了 */}
        {page === 'music' && (
          <>
            <button onClick={() => setShowHistory(true)}>History</button>
            <button className="primary" onClick={openPublish}>Publish</button>
          </>
        )}
        <button
          className="ghost"
          onClick={() => { signOut(); setSession(null); }}
        >
          Sign out
        </button>
      </header>

      <div className="body">
        {page === 'news' ? <NewsEditor onError={fail} onToast={push} /> : (
        <>
        <AlbumList
          albums={albums}
          selectedId={selectedId}
          grant={grant}
          onSelect={setSelectedId}
          onCreate={createAlbum}
          onReorder={reorderAlbums}
        />

        <main className="main">
          {!selectedAlbum ? (
            <div className="empty-state">
              <div>
                <NoteIcon size={38} color="rgba(132, 86, 89, 0.22)" />
                <p>Pick an album on the left, or create one.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="album-header">
                <div className="cover-drop" onClick={() => coverInputRef.current?.click()}>
                  {selectedAlbum.coverKey
                    ? <CoverArt
                        src={signedUrl(grant, selectedAlbum.coverKey.replace(/^audio\//, ''))}
                        alt="Album cover"
                      />
                    : <span>Add cover</span>}
                </div>
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) setCoverFile(file);
                  }}
                />

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="album-title-row">
                    <input
                      className="album-title-input"
                      defaultValue={selectedAlbum.name}
                      key={selectedAlbum.albumId}
                      onBlur={(e) => renameAlbum(e.target.value)}
                    />
                    <span className={`badge ${selectedAlbum.status}`}>
                      {selectedAlbum.status === 'published' ? 'Published' : 'Draft'}
                    </span>
                    <div className="spacer" />
                    <button
                      disabled={pending || !tracks.length}
                      onClick={retranscodeAlbum}
                      title={`Re-encode every track at ${bitrate}k`}
                    >
                      Re-encode all
                    </button>
                    <button onClick={toggleAlbumStatus}>
                      {selectedAlbum.status === 'published' ? 'Move to draft' : 'Mark published'}
                    </button>
                    <button
                      className={selectedAlbum.previewOnSite ? 'site-on' : 'ghost'}
                      disabled={selectedAlbum.status !== 'published'}
                      onClick={toggleSitePreview}
                      title={selectedAlbum.status === 'published'
                        ? 'Let visitors stream this album on mininecozyroom.com'
                        : 'Only published albums can appear on the site'}
                    >
                      {selectedAlbum.previewOnSite ? 'On the site' : 'Add to site'}
                    </button>
                    <button className="ghost danger" onClick={removeAlbum}>Delete album</button>
                  </div>
                  <div className="album-stats">
                    <span>{tracks.length} tracks</span>
                    <span>{readyCount} ready</span>
                    <span>{formatDuration(totalMs)} total</span>
                    <span>{(totalBytes / 1024 / 1024).toFixed(1)} MB xHE-AAC</span>
                  </div>
                </div>
              </div>

              <TrackList
                tracks={tracks}
                albumBitrate={bitrate}
                onRename={renameTrack}
                onReorder={reorderTracks}
                onRetranscode={retranscode}
                onDelete={removeTrack}
                onPreview={preview}
              />

              <UploadZone
                disabled={false}
                uploads={uploads}
                bitrate={bitrate}
                onBitrateChange={changeBitrate}
                onFiles={handleFiles}
              />
            </>
          )}
        </main>
        </>
        )}
      </div>

      {nowPlaying && (
        <div className="player">
          <span className="now">{nowPlaying.name}</span>
          <audio src={nowPlaying.url} controls autoPlay />
          <button
            className="close-round"
            onClick={() => setNowPlaying(null)}
            aria-label="Close player"
          >
            <CloseIcon />
          </button>
        </div>
      )}

      {coverFile && (
        <CoverDialog
          file={coverFile}
          busy={coverBusy}
          onCancel={() => setCoverFile(null)}
          onConfirm={uploadCover}
        />
      )}

      {publishPreview && (
        <PublishDialog
          preview={publishPreview}
          onClose={() => setPublishPreview(null)}
          onDone={(result) => {
            setPublishPreview(null);
            push(`Published v${result.version} with ${result.trackCount} tracks`, 'success');
          }}
        />
      )}

      {showHistory && (
        <VersionHistory
          onClose={() => setShowHistory(false)}
          onRolledBack={(version) => {
            setShowHistory(false);
            push(`Rolled back — republished as v${version}`, 'success');
          }}
        />
      )}

      {confirmDialog}
      <Toaster toasts={toasts} />
    </div>
  );
}
